import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Typography,
  Box,
  Button,
  Chip,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_ALL_EXAM_PAPERS_QUESTIONS } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";
import { useHistory } from "react-router-dom";

interface ExamPaperQuestionsTableProps {
  examPaperId: number;
}

function ExamPaperQuestionsTable({
  examPaperId,
}: ExamPaperQuestionsTableProps) {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS_QUESTIONS, {
    client: autoGradeClient,
    variables: { input: { examPaperId } },
    skip: !examPaperId,
  });

  console.log("data", data);
  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        sx={{
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          margin: 2
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </Box>
    );

  if (error)
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          margin: 2,
          padding: 4,
          textAlign: 'center',
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography color="error" variant="h6" gutterBottom>
          Error loading questions
        </Typography>
        <Typography color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );

  const rows = data?.getExamPaperQuestions?.examPaperQuestions || [];

  if (!rows.length) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 2,
          margin: 2,
          padding: 4,
          textAlign: 'center',
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Questions Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No questions found for this exam paper.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            history.push(
              `/ai-evaluation/exam-paper/view/exam-paper-question-view/update-exam-paper-question/${examPaperId}`
            )
          }
        >
          Add Questions
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            padding: 3,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Exam Paper Questions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Questions: {rows.length}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              px: 3
            }}
            onClick={() =>
              history.push(
                `/ai-evaluation/exam-paper/view/exam-paper-question-view/update-exam-paper-question/${examPaperId}`
              )
            }
          >
            Edit Questions
          </Button>
        </Box>
        {/* Table Section */}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#f5f5f5', fontWeight: 600 } }}>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    minWidth: 80,
                    fontWeight: 600,
                    borderRight: '1px solid #e0e0e0'
                  }}
                >
                  ID
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                  Paper ID
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 100 }}>
                  Q. No.
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                  Type
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 80 }}>
                  Marks
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                  Node Type
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 90 }}>
                  Choices
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 150 }}>
                  Section
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 130 }}>
                  Subject
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 130 }}>
                  Chapter
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 130 }}>
                  Topic
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                  Created By
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                  Created At
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 120 }}>
                  Updated By
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 140 }}>
                  Updated At
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: number) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#f0f0f0' },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "inherit",
                        borderRight: '1px solid #e0e0e0',
                        fontWeight: 500
                      }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell>{row.examPaperId}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.questionNumber}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.questionType}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {row.marks}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.nodeType}</TableCell>
                    <TableCell>
                      {row.choiceCount && (
                        <Chip
                          label={`${row.choiceCount} choices`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.sectionTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Order: {row.sectionOrder}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.subjectName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.chapterName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.topicName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.createdByName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.updatedByName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.updatedAt
                          ? new Date(row.updatedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                          : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Section */}
        <Box sx={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                paddingLeft: 3,
                paddingRight: 3
              }
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default ExamPaperQuestionsTable;
